// Load-bearing: LinkButton with asChild (Radix Slot) merges onClick handlers
// via prop spreading; without this directive the handlers cross the
// server/client boundary during prerender and break hydration on every page
// rendering an imageCardGrid block. See commit 1f5dae36.
"use client";

import { cn } from "@repo/ui/utils";
import { LinkButton } from "@repo/ui/link-button";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { RichTextBodyCopySimple } from "@/components/common/rich-text/rich-text-body-copy-simple.component";
import { ActionLink } from "@/components/common/actions/actions.component";
import type { ImageCardData } from "./image-card-grid.types";

interface ImageCardProps extends ImageCardData {
  /** Controls image aspect ratio — "landscape" uses 3:2, "default" uses 3:4, "small" uses 1:1 */
  size?: "landscape" | "default" | "small";
  /** Horizontal layout at mobile (small image left, text right), vertical at desktop */
  mobileHorizontal?: boolean;
  /** Override classes for the image container (e.g. responsive aspect ratios) */
  imageClassName?: string;
  /** Additional CSS class names */
  className?: string;
}

export function ImageCard({
  visual,
  headline,
  bodyCopy,
  action,
  actionLabel,
  size = "default",
  mobileHorizontal = false,
  imageClassName,
  className,
}: ImageCardProps) {
  const label = actionLabel || undefined;

  return (
    <article
      className={cn(
        "flex gap-4",
        mobileHorizontal
          ? "flex-row items-start desktop:flex-col desktop:items-stretch"
          : "flex-col",
        className,
      )}
      data-testid="image-card"
    >
      {visual && (
        <div
          className={cn(
            "overflow-hidden rounded-sm desktop:rounded-md",
            mobileHorizontal
              ? cn(
                  "w-[81px] shrink-0 aspect-[3/4] desktop:w-full desktop:shrink",
                  size === "landscape" && "desktop:aspect-[3/2]",
                  size === "default" && "desktop:aspect-[3/4]",
                )
              : cn(
                  size === "landscape" && "aspect-[3/2]",
                  size === "default" && "aspect-[3/4]",
                  size === "small" && "aspect-square",
                ),
            imageClassName,
          )}
        >
          <VisualWrapper
            visual={visual}
            className="size-full"
            visualClassName="size-full"
            imgClassName="object-cover"
          />
        </div>
      )}

      <div className={cn("flex flex-col gap-2", mobileHorizontal && "min-w-0")}>
        {headline && (
          <h3 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary">
            {headline}
          </h3>
        )}

        {bodyCopy && (
          <RichTextBodyCopySimple
            richText={bodyCopy}
            as="body-sm"
            className="text-text-secondary"
          />
        )}

        {action?.to && label && (
          <ActionLink actionTo={action.to}>
            <LinkButton asChild underline className="py-0">
              <span>{label}</span>
            </LinkButton>
          </ActionLink>
        )}
      </div>
    </article>
  );
}
